<script>
    import { createEventDispatcher } from 'svelte';
    import AccessibleModal from '../shared/AccessibleModal.svelte';
    import { idfTextStore } from '../../stores/idfTextStore.js';

    const dispatch = createEventDispatcher();

    export let show = false;

    let isDragging = false;
    let uploadedFileName = '';
    let fileContent = '';
    let isLoading = false;
    let error = null;
    let previousShow = false;

    // Load existing IDF text when modal opens (only on transition from closed to open)
    $: if (show && !previousShow) {
        // Modal just opened - load from store
        fileContent = $idfTextStore;
        error = null;
        isLoading = false;
        // If there's existing content, show a placeholder filename
        if (fileContent && !uploadedFileName) {
            uploadedFileName = 'Previously loaded content';
        }
        previousShow = true;
    } else if (!show && previousShow) {
        // Modal just closed - reset state
        uploadedFileName = '';
        isDragging = false;
        previousShow = false;
    }

    function handleDragEnter(event) {
        event.preventDefault();
        event.stopPropagation();
        isDragging = true;
    }

    function handleDragLeave(event) {
        event.preventDefault();
        event.stopPropagation();
        isDragging = false;
    }

    function handleDragOver(event) {
        event.preventDefault();
        event.stopPropagation();
    }

    function handleDrop(event) {
        event.preventDefault();
        event.stopPropagation();
        isDragging = false;

        const files = event.dataTransfer?.files;
        if (files && files.length > 0) {
            processFile(files[0]);
        }
    }

    function handleFileSelect(event) {
        const file = event.target.files?.[0];
        if (file) {
            processFile(file);
        }
    }

    async function processFile(file) {
        // Check if it's a text file - require .txt extension
        const hasTextExtension = file.name.toLowerCase().endsWith('.txt');

        if (!hasTextExtension) {
            error = 'Please upload a plain text file (.txt)';
            return;
        }

        isLoading = true;
        error = null;

        try {
            const text = await file.text();
            fileContent = text;
            uploadedFileName = file.name;
            error = null;
        } catch (err) {
            console.error('Error reading file:', err);
            error = 'Failed to read file. Please try again.';
        } finally {
            isLoading = false;
        }
    }

    function handleClose() {
        dispatch('close');
    }

    function handleSave() {
        // Save IDF text to store
        idfTextStore.set(fileContent);

        dispatch('save', { idfText: fileContent, fileName: uploadedFileName });
        handleClose();
    }

    function handleClear() {
        fileContent = '';
        uploadedFileName = '';
        error = null;
        idfTextStore.set('');
    }

    $: canSave = fileContent.trim().length > 0;
</script>

<AccessibleModal
    show={show}
    title="Upload Invention Disclosure Form"
    on:close={handleClose}
    variant="neutral"
>
    <div class="idf-upload-content">
        <!-- Action buttons at top -->
        <div class="modal-actions-top">
            <button class="btn btn-secondary" on:click={handleClose}>
                Cancel
            </button>
            <button class="btn btn-tertiary" on:click={handleClear} disabled={!fileContent}>
                Clear
            </button>
            <button
                class="modal-button modal-button-accent"
                on:click={handleSave}
                disabled={!canSave}
            >
                Save
            </button>
        </div>

        <!-- Drag and drop zone -->
        <div
            class="drop-zone"
            class:dragging={isDragging}
            class:has-content={fileContent}
            on:dragenter={handleDragEnter}
            on:dragleave={handleDragLeave}
            on:dragover={handleDragOver}
            on:drop={handleDrop}
            role="button"
            tabindex="0"
            aria-label="Drop zone for text files"
        >
            {#if isLoading}
                <div class="loading-state">
                    <i class="fas fa-spinner fa-spin"></i>
                    <span>Reading file...</span>
                </div>
            {:else if uploadedFileName}
                <div class="file-loaded">
                    <i class="fas fa-file-alt"></i>
                    <span class="file-name">{uploadedFileName}</span>
                    <span class="file-hint">Drop a new file to replace, or</span>
                    <label class="file-select-btn">
                        <input
                            type="file"
                            accept=".txt,text/plain"
                            on:change={handleFileSelect}
                            class="hidden-input"
                        />
                        Browse Files
                    </label>
                </div>
            {:else}
                <div class="drop-instructions">
                    <i class="fas fa-cloud-upload-alt"></i>
                    <span class="drop-text">Drag and drop a text file here</span>
                    <span class="drop-or">or</span>
                    <label class="file-select-btn">
                        <input
                            type="file"
                            accept=".txt,text/plain"
                            on:change={handleFileSelect}
                            class="hidden-input"
                        />
                        Browse Files
                    </label>
                </div>
            {/if}
        </div>

        {#if error}
            <div class="error-message">
                <i class="fas fa-exclamation-circle"></i>
                {error}
            </div>
        {/if}

        <!-- Preview area -->
        {#if fileContent}
            <div class="preview-section">
                <label for="idf-preview" class="preview-label">Content Preview</label>
                <textarea
                    id="idf-preview"
                    bind:value={fileContent}
                    class="preview-textarea"
                    rows="12"
                    placeholder="IDF content will appear here..."
                />
            </div>
        {/if}
    </div>
</AccessibleModal>

<style>
.idf-upload-content {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-4);
    padding: var(--spacing-4);
    max-height: 70vh;
}

.modal-actions-top {
    display: flex;
    justify-content: flex-end;
    gap: var(--spacing-3);
    padding-bottom: var(--spacing-3);
    border-bottom: 1px solid var(--border-color-default);
}

.drop-zone {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 180px;
    padding: var(--spacing-6);
    background-color: var(--color-bg-primary);
    border: 2px dashed var(--border-color-default);
    border-radius: var(--border-radius-xl);
    transition: all 0.2s var(--ease-in-out);
    cursor: pointer;
}

.drop-zone:hover {
    border-color: var(--color-terms-500);
    background-color: var(--color-bg-hover-light);
}

.drop-zone.dragging {
    border-color: var(--color-terms-600);
    background-color: var(--color-terms-50);
    border-style: solid;
}

.drop-zone.has-content {
    border-style: solid;
    border-color: var(--color-success);
    background-color: var(--color-success-background);
}

.drop-instructions {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--spacing-3);
    color: var(--color-text-secondary);
}

.drop-instructions i {
    font-size: 3rem;
    color: var(--color-text-tertiary);
}

.drop-text {
    font-size: var(--font-size-base);
    font-weight: 500;
}

.drop-or {
    font-size: var(--font-size-sm);
    color: var(--color-text-tertiary);
}

.file-select-btn {
    padding: var(--spacing-2) var(--spacing-4);
    font-size: var(--font-size-sm);
    font-weight: 600;
    color: var(--color-text-primary);
    background-color: var(--color-btn-default);
    border-radius: var(--border-radius-xl);
    cursor: pointer;
    transition: background-color 0.2s var(--ease-in-out);
}

.file-select-btn:hover {
    background-color: var(--color-btn-default-hover);
}

.hidden-input {
    display: none;
}

.loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--spacing-2);
    color: var(--color-text-secondary);
}

.loading-state i {
    font-size: 2rem;
    color: var(--color-terms-500);
}

.file-loaded {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--spacing-2);
}

.file-loaded i {
    font-size: 2.5rem;
    color: var(--color-success);
}

.file-name {
    font-size: var(--font-size-base);
    font-weight: 600;
    color: var(--color-text-primary);
}

.file-hint {
    font-size: var(--font-size-sm);
    color: var(--color-text-tertiary);
}

.error-message {
    display: flex;
    align-items: center;
    gap: var(--spacing-2);
    padding: var(--spacing-3);
    font-size: var(--font-size-sm);
    color: var(--color-error);
    background-color: var(--color-error-background);
    border-radius: var(--border-radius-md);
}

.preview-section {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-2);
}

.preview-label {
    font-size: var(--font-size-sm);
    font-weight: 600;
    color: var(--color-text-primary);
}

.preview-textarea {
    padding: var(--spacing-3);
    font-size: var(--font-size-sm);
    font-family: inherit;
    color: var(--color-text-primary);
    background-color: var(--color-bg-primary);
    border: 1px solid var(--border-color-default);
    border-radius: var(--border-radius-md);
    resize: vertical;
    transition: border-color 0.2s;
}

.preview-textarea:focus {
    outline: none;
    border-color: var(--color-terms-600);
}

.preview-textarea::placeholder {
    color: var(--color-text-tertiary);
}

.btn {
    padding: var(--spacing-2) var(--spacing-4);
    font-size: var(--font-size-sm);
    font-weight: 600;
    border: none;
    border-radius: var(--border-radius-xl);
    transition: all 0.2s var(--ease-in-out);
    cursor: pointer;
}

.btn-secondary {
    color: var(--color-text-secondary);
    background-color: var(--color-btn-default);
}

.btn-secondary:hover {
    background-color: var(--color-btn-default-hover);
}

.btn-tertiary {
    color: var(--color-text-secondary);
    background-color: transparent;
    border: 1px solid var(--border-color-default);
}

.btn-tertiary:hover:not(:disabled) {
    background-color: var(--color-bg-hover-light);
}

.btn-tertiary:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}
</style>
